import { apiClient, type ApiResponse } from "./apiClient";

/** Shown until product confirms the pack name. */
export const IMPACT_ASSESSMENT_PACKAGE_NAME = "Impact Assessment Package";

export interface AssessmentPack {
  id: string;
  name: string;
  impactItems: string[];
  /** Set when the payload includes an explicit count. A single `reference_object` counts as 1. */
  referenceCount: number | null;
  /** `reference_object.name`, falling back to its id. */
  referenceObjectLabel: string;
  detailUrl: string;
}

function normalizeVersion(version: string): string {
  const trimmed = version.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("v") || trimmed.startsWith("V")
    ? trimmed.slice(1)
    : trimmed;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickString(item: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function labelFromUnknown(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  const record = asRecord(value);
  if (!record) return "";
  return pickString(record, [
    "label",
    "name",
    "display_name",
    "displayName",
    "code",
  ]);
}

/** Impact Items use `impacted_object_types[].object_type_name`. */
function pickImpactItemLabels(value: unknown): string[] {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  return values
    .map((item) => {
      const record = asRecord(item);
      if (!record) return labelFromUnknown(item);
      return (
        pickString(record, [
          "object_type_name",
          "objectTypeName",
          "display_name",
          "displayName",
          "name",
          "label",
        ]) || ""
      );
    })
    .filter(Boolean);
}

function mapReferenceObject(value: unknown): { label: string; id: string; version: string } | null {
  const record = asRecord(value);
  if (!record) return null;
  const id = pickString(record, ["id"]);
  const version = pickString(record, ["version"]);
  const label =
    pickString(record, ["name", "display_name", "displayName", "label"]) || id;
  if (!label && !id && !version) return null;
  return { label, id, version };
}

function pickCount(item: Record<string, unknown>): number | null {
  for (const key of [
    "reference_count",
    "referenceCount",
    "ref_count",
    "refCount",
    "reference_object_count",
    "referenceObjectCount",
  ]) {
    const value = item[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

/** Only http(s) detail links are opened. Other schemes are dropped. */
export function sanitizeAssessmentPackDetailUrl(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

const PACK_LIST_KEYS = [
  "assessment_packs",
  "assessmentPacks",
  "packs",
  "items",
  "results",
] as const;

function extractPackRecords(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload
      .map(asRecord)
      .filter((item): item is Record<string, unknown> => item != null);
  }

  const root = asRecord(payload);
  if (!root) return [];

  for (const key of PACK_LIST_KEYS) {
    const nested = root[key];
    if (!Array.isArray(nested)) continue;
    return nested
      .map(asRecord)
      .filter((item): item is Record<string, unknown> => item != null);
  }

  if (
    pickString(root, [
      "detail_url",
      "detailUrl",
      "name",
      "pack_name",
      "packName",
      "title",
    ])
  ) {
    return [root];
  }

  return [];
}

function mapOnePack(
  item: Record<string, unknown>,
  index: number,
): AssessmentPack | null {
  const detailUrl = sanitizeAssessmentPackDetailUrl(
    pickString(item, ["detail_url", "detailUrl"]),
  );
  const explicitName = pickString(item, [
    "name",
    "pack_name",
    "packName",
    "title",
  ]);
  const impactItems = pickImpactItemLabels(
    item.impacted_object_types ??
      item.impactedObjectTypes ??
      item.impact_items ??
      item.impactItems ??
      item.impacted_items ??
      item.impactedItems ??
      item.objects,
  );
  const referenceObject = mapReferenceObject(
    item.reference_object ?? item.referenceObject,
  );
  const explicitCount = pickCount(item);
  const referenceCount =
    explicitCount ?? (referenceObject ? 1 : null);

  if (
    !explicitName &&
    !detailUrl &&
    impactItems.length === 0 &&
    referenceCount == null &&
    !referenceObject
  ) {
    return null;
  }

  const referenceId = [referenceObject?.id, referenceObject?.version]
    .filter(Boolean)
    .join("-");

  return {
    id:
      pickString(item, ["id", "pack_id", "packId", "uuid"]) ||
      referenceId ||
      detailUrl ||
      `assessment-pack-${index}`,
    name: explicitName || IMPACT_ASSESSMENT_PACKAGE_NAME,
    impactItems,
    referenceCount,
    referenceObjectLabel: referenceObject?.label ?? "",
    detailUrl,
  };
}

export function mapAssessmentPacks(payload: unknown): AssessmentPack[] {
  return extractPackRecords(payload).flatMap((item, index) => {
    const pack = mapOnePack(item, index);
    return pack ? [pack] : [];
  });
}

/** Matches GET .../assessment-packs?page=1&page_size=20 */
export const ASSESSMENT_PACKS_PAGE = 1;
export const ASSESSMENT_PACKS_PAGE_SIZE = 20;

/**
 * GET /regulations/{id}/{version}/assessment-packs?page=1&page_size=20
 * View Details uses each item's `detail_url`.
 */
export async function getRegulationAssessmentPacks(
  regulationId: string,
  version: string,
): Promise<ApiResponse<AssessmentPack[] | null>> {
  const normalizedVersion = normalizeVersion(version);
  const result = await apiClient.get<unknown>(
    `/regulations/${encodeURIComponent(regulationId)}/${encodeURIComponent(normalizedVersion)}/assessment-packs`,
    {
      page: ASSESSMENT_PACKS_PAGE,
      page_size: ASSESSMENT_PACKS_PAGE_SIZE,
    },
  );

  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    data: result.data == null ? null : mapAssessmentPacks(result.data),
  };
}
